import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Mail } from '@/components/icons';
import { Button } from '@/components/ui/Button';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Textarea } from '@/components/form/Textarea';
import { previewStageEmail, type StageEmailInfo } from '@/api/admin';
import { apiError } from '@/lib/apiError';

export interface StageTemplate {
  subject: string;
  body: string;
}

const VARS = ['{{stage}}', '{{docNo}}', '{{action}}', '{{partyName}}', '{{actionedBy}}', '{{details}}'];
const VAR_HINT: Record<string, string> = {
  '{{stage}}': 'e.g. Outward GRN', '{{docNo}}': 'document number', '{{action}}': 'e.g. completed',
  '{{partyName}}': 'customer / vendor', '{{actionedBy}}': 'who did it', '{{details}}': 'list: party, who, key facts',
};

// The preview card shows just the email (like the document previews), not the grey mail-client backdrop.
const PREVIEW_CSS = '<style>html,body{margin:0;background:transparent!important}body>table{padding:0!important;background:transparent!important}</style>';
const withPreviewCss = (html: string) => (html.includes('<html>') ? html.replace('<html>', `<html><head>${PREVIEW_CSS}</head>`) : PREVIEW_CSS + html);

interface Props {
  info: StageEmailInfo;
  /** Saved wording; blank fields mean the default. */
  value: StageTemplate | undefined;
  onChange: (t: StageTemplate) => void;
  onSave: () => void;
  saving: boolean;
}

/** One internal (handoff) email — laid out like the document templates: editor card + live preview column. */
export function InternalEmailPanel({ info, value, onChange, onSave, saving }: Props) {
  const navigate = useNavigate();
  const subject = value?.subject || info.defaultSubject;
  const body = value?.body || info.defaultBody;
  const isDefault = subject === info.defaultSubject && body === info.defaultBody;
  const [focused, setFocused] = useState<'subject' | 'body'>('body');
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const [frameH, setFrameH] = useState(420);

  // Preview follows the typing, a moment after it stops; rendered by the server exactly as sent.
  const [draft, setDraft] = useState({ subject, body });
  useEffect(() => {
    const t = setTimeout(() => setDraft({ subject, body }), 350);
    return () => clearTimeout(t);
  }, [subject, body]);
  const preview = useQuery({
    queryKey: ['stage-email-preview', info.key, draft.subject, draft.body],
    queryFn: () => previewStageEmail(info.key, draft.subject, draft.body),
    placeholderData: (prev) => prev,
  });

  const set = (patch: Partial<StageTemplate>) => onChange({ subject, body, ...patch });
  const insert = (v: string) => {
    if (focused === 'subject') return set({ subject: subject + v });
    const el = bodyRef.current;
    const at = el ? el.selectionStart : body.length;
    set({ body: body.slice(0, at) + v + body.slice(at) });
  };

  return (
    <div className="flex flex-1 gap-4">
      {/* Editor */}
      <div className="flex-1 rounded-xl border border-[var(--border-soft)] bg-[var(--bg-card)] p-5">
        <div className="mb-1 flex items-center gap-2">
          <span className="text-[var(--text-muted)]"><Mail size={15} /></span>
          <h3 className="text-base font-extrabold text-[var(--text-primary)]">{info.label}</h3>
          <span className="text-sm text-[var(--text-muted)]">— {info.action}</span>
          <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-bold ${info.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-[var(--bg-soft)] text-[var(--text-muted)]'}`}>
            {info.enabled ? 'On' : 'Off'}
          </span>
        </div>
        <p className="mb-4 text-xs text-[var(--text-muted)]">
          Sent automatically to the next desk the moment this step happens. Turn it on or off, or set fixed recipients, in{' '}
          <button type="button" className="font-semibold text-accent underline" onClick={() => navigate('/admin/configurations/email')}>
            Email settings
          </button>
          .
        </p>

        <dl className="mb-4 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 rounded-lg border border-[var(--border-soft)] bg-[var(--bg-soft)] p-3 text-xs">
          <dt className="font-bold text-[var(--text-secondary)]">To</dt>
          <dd className="text-[var(--text-primary)]">
            {info.goesTo}
            {info.recipients.length > 0 && <span className="text-[var(--text-muted)]"> — {info.recipients.join(', ')}</span>}
            {info.recipients.length === 0 && !info.goesTo.includes('@') && <span className="text-danger"> — nobody has this permission yet</span>}
          </dd>
          <dt className="font-bold text-[var(--text-secondary)]">CC</dt>
          <dd className="text-[var(--text-primary)]">{info.cc.join(', ')}</dd>
        </dl>

        {/* Variable chips — same as the document templates */}
        <div className="mb-4 rounded-lg border border-[var(--border-soft)] bg-[var(--bg-soft)] p-3">
          <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
            Available variables — click to insert into the focused field
          </p>
          <div className="flex flex-wrap gap-1.5">
            {VARS.map((v) => (
              <button
                key={v}
                type="button"
                title={VAR_HINT[v]}
                disabled={focused === 'subject' && v === '{{details}}'}
                onClick={() => insert(v)}
                className="rounded border border-[var(--border-soft)] bg-[var(--bg-card)] px-2 py-0.5 font-mono text-[10px] text-[var(--text-secondary)] transition-colors hover:border-accent hover:text-accent disabled:opacity-40"
              >
                {v}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          <FormGroup label="Email Subject">
            <Input value={subject} maxLength={200} onFocus={() => setFocused('subject')} onChange={(e) => set({ subject: e.target.value })} />
          </FormGroup>
          <FormGroup label="Email Body" hint="The “Open” button and the footer are added below the message automatically.">
            <Textarea ref={bodyRef} rows={6} value={body} maxLength={4000} onFocus={() => setFocused('body')} onChange={(e) => set({ body: e.target.value })} />
          </FormGroup>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="glass" disabled={isDefault} onClick={() => onChange({ subject: '', body: '' })}>
            Reset to default
          </Button>
          <Button variant="primary" onClick={onSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Templates'}
          </Button>
        </div>
      </div>

      {/* Live preview — same column as the document templates */}
      <div className="w-72 shrink-0">
        <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)]">Live Email Preview</p>
        <div className="mb-2 rounded-lg border border-[var(--border-soft)] bg-[var(--bg-card)] px-3 py-2 text-[11px] text-[var(--text-primary)]">
          <span className="font-bold">Subject: </span>
          {preview.data?.subject ?? '…'}
        </div>
        {preview.isError ? (
          <div className="rounded-xl border border-[var(--border-soft)] p-4 text-xs text-danger">{apiError(preview.error, 'Could not build the preview')}</div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-[var(--border-soft)] shadow-sm">
            <iframe
              title={`${info.label} email preview`}
              className="block w-full border-0"
              style={{ height: frameH }}
              sandbox="allow-same-origin"
              srcDoc={withPreviewCss(preview.data?.html ?? '')}
              onLoad={(e) => {
                const doc = e.currentTarget.contentDocument;
                if (doc?.body) setFrameH(doc.body.scrollHeight);
              }}
            />
          </div>
        )}
        <p className="mt-2 text-[10px] text-[var(--text-muted)]">Example data — real emails use the document’s own details.</p>
      </div>
    </div>
  );
}
